import { useCallback, useEffect, useState } from 'react';

import styled from '@emotion/styled';
import { dropdownMenu2Classes, type PropsWithClassName } from '@kc-console/common-ui';
import { useScopedLinkBuilder } from '@kc-console/context';
import { np } from '@kc-console/core';
import { SpriteSvgIcon } from '@kc-console/shared-ui';

import { ServiceButton } from '../ServiceButton';
import FavoriteItemMenu from './FavoriteItemMenu';
import { useFavoriteItemDnd } from './useFavoriteItemDnd';

import type { MouseEvent, RefCallback } from 'react';
import type { FavoriteService } from '@kc-console/context';
import type { DropSide } from './constants';

interface FavoriteItemProps extends PropsWithClassName {
  index: number;
  onDragEnd: (didDrop: boolean) => void;
  onDragHover: (from: number, to: number, side: DropSide) => void;
  service: FavoriteService;
  color?: 'default' | 'global';
  isVisible?: boolean;
  ref?: RefCallback<HTMLButtonElement>;
}

/**
 * 즐겨찾기 서비스 아이템 컴포넌트
 * @description
 * - 즐겨찾기 서비스를 표시하고 클릭 시 해당 서비스로 이동
 * - 우클릭 시 즐겨찾기 제거 메뉴를 표시
 * - 드래그하여 순서를 변경 (drop은 목록 컨테이너가 처리, 아이템은 hover로 삽입 위치만 결정)
 */
const FavoriteItem = ({
  service,
  index,
  onDragHover,
  onDragEnd,
  ref,
  isVisible = true,
  color = 'default',
}: FavoriteItemProps) => {
  const { getProps } = useScopedLinkBuilder();
  // TODO(navigation): ServiceButton(=ButtonGhost) 이 anchor item 지원하도록 개선되면
  //   isAnchor 옵션 제거하고 href 로 전환. 현재 isAnchor:false 는 ctrl/cmd+click 새 탭만
  //   emulate 하며 shift→새 창, alt→다운로드, middle-click 은 지원하지 못한다.
  const linkProps = getProps(service.path, { isAnchor: false });

  const [isOpenMenu, setIsOpenMenu] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLButtonElement | null>(null);

  // 드래그 소스 + hover(가로 midpoint) — drop은 목록 컨테이너(FavoriteList)에 위임
  const { connectDnd, isDragging } = useFavoriteItemDnd({
    axis: 'x',
    index,
    onDragEnd,
    onDragHover,
    service,
    sourceArea: 'bar',
  });

  const handleButtonRef = useCallback(
    (element: HTMLButtonElement | null) => {
      // itemRefs 등록(checkOverflow 폭 측정의 원천)
      ref?.(element);
      setMenuAnchorEl(element);
      connectDnd(element);
    },
    [ref, connectDnd],
  );

  const handleRemoveFavoriteMenuOpen = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const target = e.currentTarget;
    setMenuAnchorEl(target);
    setIsOpenMenu(true);
  };

  useEffect(() => {
    if (!isVisible) {
      setIsOpenMenu(false);
    }
  }, [isVisible]);

  return (
    <>
      <BarItemServiceButton
        ref={handleButtonRef}
        onClick={linkProps.onClick}
        onContextMenu={e => handleRemoveFavoriteMenuOpen(e)}
        isDragging={isDragging}
        startIcon={
          <SpriteSvgIcon
            fontSize={16}
            id={service.keyword}
            category={'BeyondServiceColoredRegular'}
          />
        }
      >
        {service.name}
      </BarItemServiceButton>
      <FavoriteItemMenu
        service={service}
        open={isOpenMenu}
        onClose={() => setIsOpenMenu(false)}
        anchorEl={menuAnchorEl}
        color={color}
        css={{
          [`.${dropdownMenu2Classes.paper}`]: {
            width: 160,
          },
        }}
      />
    </>
  );
};

export default FavoriteItem;

/* 호버는 버튼 기본 pointer, 누르는 동안(드래그 시작 전)만 grab.
   네이티브 드래그 시작 후의 커서는 브라우저가 제어하므로 CSS로 바꿀 수 없다. */
const BarItemServiceButton = styled(ServiceButton, { shouldForwardProp: np('isDragging') })<{
  isDragging: boolean;
}>`
  /* 드래그 중인 소스 버튼은 흐리게 표시한다 */
  opacity: ${({ isDragging }) => (isDragging ? 0.4 : 1)};

  &:active {
    cursor: grab;
  }
`;
